import React, { type ComponentType, type JSX } from 'react';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import type { ThemeKey } from 'containers/Settings/ChildPages/General/themeChoiceConfig';

export type ThemeChoiceOptionProps = {
  id: string;
  value: ThemeKey;
  label: string;
  Icon: ComponentType;
  selectedThemeKey: string | undefined;
  onSelect: (themeKey: ThemeKey) => void;
};

const ThemeChoiceOption = ({
  id,
  value,
  label,
  Icon,
  selectedThemeKey,
  onSelect,
}: ThemeChoiceOptionProps): JSX.Element => {
  const theme = useTheme();
  const isSelected = selectedThemeKey === value;

  return (
    <Box
      component='button'
      type='button'
      role='radio'
      id={id}
      aria-checked={isSelected}
      onClick={() => onSelect(value)}
      sx={{
        flex: 1,
        minWidth: 0,
        margin: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        padding: theme.spacing(2),
        borderRadius: '12px',
        border: '1px solid',
        borderColor: isSelected ? theme.palette.primary.main : 'transparent',
        bgcolor: isSelected ? theme.palette.primary.light : theme.palette.background.default,
        cursor: 'pointer',
        textAlign: 'center',
      }}
    >
      <Typography
        variant='paragraphMedium'
        sx={{
          wordBreak: 'normal',
          color: isSelected ? theme.palette.primary.main : theme.palette.text.secondary,
        }}
      >
        {label}
      </Typography>
      <Avatar
        variant='rounded'
        sx={{
          width: theme.spacing(5),
          height: theme.spacing(5),
          bgcolor: isSelected ? theme.palette.primary.main : theme.palette.background.layer2,
          color: theme.palette.common.white,
        }}
      >
        <Icon />
      </Avatar>
    </Box>
  );
};

export default ThemeChoiceOption;
